import { DateTimePickerDE } from "@/shared/data-entries/common/DateTimePickerDE";
import { SampleSelector } from "@/shared/selectors/SampleSelector";
import { Box, Grid, TextField } from "@mui/material";
import { Control, Controller, UseFormSetValue } from "react-hook-form";
import { ChemistrySample } from "../../models/lab.models";

interface SampleStepProps {
  control: Control<ChemistrySample>;
  setValue: UseFormSetValue<ChemistrySample>;
  existingSamples?: { sampleTypeName: string; sampleSequence?: number }[];
}

export const SampleStep = ({
  control,
  setValue,
  existingSamples = [],
}: SampleStepProps): JSX.Element => {
  const handleSampleChange = (value: number | null, sampleTypeName: string) => {
    setValue("SampleTypeId", value);
    setValue("SampleTypeName", sampleTypeName);

    const matchingSamples = existingSamples.filter(
      (sample) => sample.sampleTypeName === sampleTypeName,
    );

    if (matchingSamples.length > 0) {
      const highestSequenceSample = matchingSamples.reduce((prev, current) => {
        const prevSequence =
          prev.sampleSequence !== undefined ? prev.sampleSequence : -1;
        const currentSequence =
          current.sampleSequence !== undefined ? current.sampleSequence : -1;
        return currentSequence > prevSequence ? current : prev;
      });

      const nextOrder = (highestSequenceSample.sampleSequence || 0) + 1;
      setValue("SampleSequence", nextOrder);
    } else {
      setValue("SampleSequence", 1);
    }
  };

  return (
    <Box mt={3}>
      <Grid
        container
        spacing={3}
        style={{ display: "flex", justifyContent: "center" }}
      >
        <Grid item xs={12} sm={4} md={4}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Controller
                name="Reported"
                control={control}
                rules={{ required: "Date & Time is required." }}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePickerDE
                    label="Date & Time"
                    value={field.value}
                    format="MM/DD/YYYY HH:mm"
                    onChange={field.onChange}
                    fieldError={error}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="SampleTypeId"
                control={control}
                rules={{ required: "Sample Code is required." }}
                render={({ field, fieldState: { error } }) => (
                  <SampleSelector
                    value={field.value}
                    onChange={(newValue, newSampleTypeName) => {
                      field.onChange(newValue);
                      handleSampleChange(newValue, newSampleTypeName);
                    }}
                    fieldError={error}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={4} md={4}>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Controller
                name="Technician"
                control={control}
                rules={{
                  required: "Technician is required.",
                  maxLength: 50,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Technician"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={field.value ?? ""}
                    error={!!error}
                    helperText={error?.message}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="SampleSequence"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Order"
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="number"
                    value={field.value ?? ""}
                    error={!!error}
                    helperText={error?.message}
                    onChange={field.onChange}
                    disabled
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
